import React, { useState, useEffect } from 'react';

const WeddingBackground = ({ children, className = '' }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Detect device type and screen size
    const detectDevice = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      // Detect all iOS devices (iPhone, iPad, iPod)
      const isIOSDevice = /iPhone|iPad|iPod/i.test(userAgent) || 
                          (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPad on iOS 13+
      const isMobileDevice = window.innerWidth < 768;
      
      setIsIOS(isIOSDevice);
      setIsMobile(isMobileDevice);
    };

    // Initial detection
    detectDevice();

    // Listen for window resize
    const handleResize = () => {
      detectDevice();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    // Preload images with format detection
    const loadImage = (src) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(src);
        img.onerror = reject;
        img.src = src;
      });
    };

    const tryLoadImages = async () => {
      // Choose images based on device type
      let imagesToTry;
      
      if (isMobile || isIOS) {
        // For mobile/iOS: try vertical images first, then horizontal as fallback
        imagesToTry = [
          // Try vertical WebP format first (best compression for mobile)
          '/wedding-photos/hero-bg-vertical.webp',
          // Fallback to vertical JPEG
          '/wedding-photos/hero-bg-vertical.jpg',
          // High-DPI vertical WebP
          window.devicePixelRatio > 1 ? '/wedding-photos/hero-bg-vertical@2x.webp' : null,
          // High-DPI vertical JPEG
          window.devicePixelRatio > 1 ? '/wedding-photos/hero-bg-vertical@2x.jpg' : null,
          // Fallback to horizontal images if vertical not available
          '/wedding-photos/hero-bg.webp',
          '/wedding-photos/hero-bg.jpg',
        ].filter(Boolean);
      } else {
        // For desktop: use horizontal images
        imagesToTry = [
          // Try WebP format first (best compression)
          '/wedding-photos/hero-bg.webp',
          // Fallback to JPEG
          '/wedding-photos/hero-bg.jpg',
          // High-DPI WebP
          window.devicePixelRatio > 1 ? '/wedding-photos/hero-bg@2x.webp' : null,
          // High-DPI JPEG
          window.devicePixelRatio > 1 ? '/wedding-photos/hero-bg@2x.jpg' : null,
        ].filter(Boolean);
      }

      for (const src of imagesToTry) {
        try {
          const loadedSrc = await loadImage(src);
          setCurrentImage(loadedSrc);
          setImageLoaded(true);
          console.log(`✅ 婚纱照背景加载成功: ${src}`);
          return;
        } catch (error) {
          // 静默处理，不在控制台输出错误
        }
      }
      
      // If all images fail to load, use default background
      console.log('💡 未找到背景图片，使用默认渐变背景');
      setImageError(true);
      setImageLoaded(true);
    };

    tryLoadImages();
  }, [isMobile, isIOS]);

  // For iOS devices, we need a different approach since background-attachment: fixed doesn't work
  const backgroundStyle = currentImage && !imageError && !isIOS
    ? {
        backgroundImage: `linear-gradient(
          135deg,
          rgba(222, 41, 16, 0.3) 0%,
          rgba(255, 222, 0, 0.2) 25%,
          rgba(255, 107, 157, 0.15) 50%,
          rgba(222, 41, 16, 0.25) 100%
        ), url(${currentImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top 0px',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',  
        transition: 'opacity 0.6s ease-in-out',
        opacity: imageLoaded ? 1 : 0.8,
      }
    : {}; // 如果没有图片或是iOS设备，不设置背景样式

  // 确定要使用的CSS类
  const backgroundClass = currentImage && !imageError 
    ? (isIOS ? 'min-h-screen' : 'min-h-screen relative')
    : 'min-h-screen wedding-bg-default wedding-bg-pattern';

  // For iOS, we need to render the fixed background before the main container
  if (isIOS && currentImage && !imageError) {
    return (
      <div className="relative min-h-screen">
        {/* Fixed background layer for iOS - rendered with proper z-index */}
        <div 
          className="fixed inset-0 w-full h-full"
          style={{
            backgroundImage: `linear-gradient(
              135deg,
              rgba(222, 41, 16, 0.3) 0%,
              rgba(255, 222, 0, 0.2) 25%,
              rgba(255, 107, 157, 0.15) 50%,
              rgba(222, 41, 16, 0.25) 100%
            ), url(${currentImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top 0px',
            backgroundRepeat: 'no-repeat',
            transition: 'opacity 0.6s ease-in-out',
            opacity: imageLoaded ? 1 : 0.8,
            zIndex: 0, // Changed from -1 to 0
            willChange: 'transform', // Optimize for iOS
            transform: 'translateZ(0)', // Force hardware acceleration
            WebkitTransform: 'translateZ(0)', // Safari prefix
          }}
        />
        
        {/* Pattern overlay for iOS */}
        <div 
          className="fixed inset-0 w-full h-full opacity-20 pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ff6b9d' fill-opacity='0.03'%3E%3Cpath d='M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z'/%3E%3C/g%3E%3C/svg%3E")`,
            zIndex: 1, // Changed from -1 to 1
          }}
        />

        {/* Main content container with proper z-index */}
        <div className={`${backgroundClass} ${className} relative`} style={{ 
          backgroundColor: 'transparent',
          zIndex: 2, // Ensure content is above background
          position: 'relative'
        }}>
          {children}
        </div>
      </div>
    );
  }

  // For non-iOS devices or when no image
  return (
    <div 
      className={`${backgroundClass} ${className}`}
      style={backgroundStyle}
    >
      {/* Loading overlay - 只在加载图片时显示 */}
      {!imageLoaded && currentImage && !imageError && (
        <div className="absolute inset-0 bg-gradient-to-b from-red-50 to-pink-50 animate-pulse" />
      )}
      
      {/* Background pattern overlay for texture - 只在有背景图时添加纹理 */}
      {currentImage && !imageError && (
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ff6b9d' fill-opacity='0.03'%3E%3Cpath d='M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z'/%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      )}

      {children}
    </div>
  );
};

export default WeddingBackground;
